<template>
  <div style="position: relative; border-radius: 12px; overflow: hidden;">
    <TeamNewInviteModal v-if="showTeamInviteModal" @refresh-data="refreshData" @close="showTeamInviteModal = false" :open-slots="openSlots" />
    <TeamMemberModal v-if="showMemberModal" @refresh-data="refreshData" :team-member="modalMember" :member-status="modalStatus" :open-slots="openSlots" @close="showMemberModal = false"/>
    <LoadingSpinner v-if="loading" />
    <section v-if="isGrace && !loading" class="connections-section">
      <div>
        <b-alert show class="gk-alert mb-0" variant="warning">
          <div class="grace-payment-alert">
            <p class="mb-0" v-translate>
              Your last Premium payment was not successful. You need Premium in order to manage your team and invite users. Please complete the Premium payment first to manage your team.
            </p>
            <GekkoButton
              :href="Urls['subscriptions:manage']()"
              :text="$gettext('Go to Subscriptions')"
            />
          </div>
        </b-alert>
        <!-- GRACE Your next payment will be on <strong>{{ activePlan.end_date | formatDate }}</strong> -->
      </div>
    </section>

    <section class="connections-section">
      <div class="setting-content--section">
        <div class="connections-section--header">
          <div class="connections-section--header__title">
            <h3>{{ $gettext('Active team members') }}</h3>
            <p>{{ $gettext('These are the members that count towards your subscription.') }}</p>
          </div>
          <div>
            <GekkoButton
              :disabled="isGrace"
              @click="openInviteModal"
              :text="$gettext('Add team member')"
              :icon="'fa-solid fa-user-plus'"
            />
          </div>
        </div>
        <div class="connections-list">
          <!-- Currently active connections -->
          <UserCard 
            v-for="(member, index) in activeTeamMembers" :key="'conn-active-' + index"
            :connection="member" 
            @click.native="openMemberModal(member.id, 'active')"
          />
          <!-- Pending invites -->
          <UserCard 
            v-for="(member, index) in pendingInvites" :key="'pending-conn-' + index"
            :connection="member" :status="'pending'"
            @click.native="openMemberModal(member.id, 'pending')"
          />
          <!-- Open invites -->
          <UserCard 
            v-for="index in openSlots" :key="'open-' + index"
            :status="'open'"
            @click.native="openInviteModal"
          />
        </div>
      </div>
    </section>
    <section v-if="inactiveTeamMembers.length > 0" class="connections-section">
      <div class="setting-content--section">
        <div class="connections-section--header">
          <div class="connections-section--header__title">
            <h3>{{ $gettext('Inactive team members') }}</h3>
            <p>{{ $gettext('These are inactive members that have no longer access to your companies nor count for your subscription. Reactivate or delete them.') }}</p>
          </div>
        </div>
        <div class="connections-list">
          <!-- Inactive connections -->
          <UserCard 
            v-for="(member, index) in inactiveTeamMembers" :key="'conn-inactive-' + index"
            :connection="member" :status="'inactive'"
            @click.native="openMemberModal(member.id, 'inactive')"
          />
        </div>
      </div>
    </section>

    <section v-if="showOtherTeamMembers && otherTeamMembers.length > 0" class="connections-section">
      <div class="setting-content--section">
        <div class="connections-section--header">
          <div class="connections-section--header__title">
            <h3>{{ $gettext('Other team members') }}</h3>
            <p>{{ $gettext('These are the members connected to companies that are not yours') }}</p>
          </div>
        </div>
        <div class="connections-list">
          <!-- Other team members -->
          <UserCard 
            v-for="(member, index) in otherTeamMembers" :key="'conn-other-' + index"
            :connection="member"
            @click.native="openMemberModal(member.id, 'other')"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex"
import GekkoButton from "@/components/GekkoButton"

import UserCard from "@/components/team/UserCard"

import TeamNewInviteModal from "@/components/team/TeamNewInviteModal"
import TeamMemberModal from "@/components/team/TeamMemberModal"

import settingsPageSetup from "@/mixins/settingsPageSetup"
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"

export default {
  name: "Team",
  components: { 
    GekkoButton,
    UserCard,
    TeamNewInviteModal,
    TeamMemberModal,
    LoadingSpinner
  },
  mixins: [ settingsPageSetup ],
  data () {
    return {
      pageSettings: {
        title: this.$gettext("Team"),
        showCompanyPicker: false
      },
      loading: true,
      showTeamInviteModal: false,
      showMemberModal: false,
      modalMemberId: null,
      modalStatus: null,
      showOtherTeamMembers: false
    }
  },
  computed: {
    ...mapGetters({ 
      isPremium: 'getIsPremium',
      teamMembers: 'team/getConnectionsPerUser',
      activeTeamMembers: 'team/getActiveTeamMembers',
      inactiveTeamMembers: 'team/getInactiveTeamMembers',
      otherTeamMembers: 'team/getOtherTeamMembers',
      pendingInvites: 'team/getInvitationsPerUser',
      minTeamSize: 'subscriptionStore/getMinTeamSize',
      activePlan: 'subscriptionStore/getActivePlan',
    }),
    openSlots () {
      const usedSlots = this.activeTeamMembers.length + this.pendingInvites.length
      let openSlots = this.minTeamSize - usedSlots
      openSlots++ // including 'my profile'
      return (openSlots > 0) ? openSlots : 0
    },
    modalMember () {
      let modal_member = {}
      if (this.modalMemberId != null) {
        if (this.modalStatus === 'pending') {
          modal_member = this.pendingInvites.find(item => item.id === this.modalMemberId)
        } else {
          modal_member = this.teamMembers.find(item => item.id === this.modalMemberId)
        }
      }
      return modal_member
    },
    isGrace () {
      return (this.activePlan && this.activePlan.grace_date !== null)
    },
  },
  created () {
    this.initData()
  },
  methods: {
    ...mapActions({ 
      loadTeamMainData: 'team/loadTeamMainData',
      loadSubscriptionsMainData: 'subscriptionStore/loadSubscriptionsMainData'
    }),
    initData () {
      this.loading = true
      this.loadTeamMainData().then(() => {
        this.loadSubscriptionsMainData().then(() => {
          this.$nextTick(() => {
            this.loading = false
          })
        })
      })
    },
    refreshData (payload = null) {
      this.loadTeamMainData().then(() => {
        if (payload) {
          if (payload.modal_status) {
            this.modalStatus = payload.modal_status
          }
        }
      })
    },
    openInviteModal () {
      if(this.isGrace){
        return;
      }
      this.showTeamInviteModal = true
    },
    openMemberModal (member_id, status) {
      if(this.isGrace){
        return;
      }
      this.modalStatus = status
      this.modalMemberId = member_id
      this.showMemberModal = true
    }
  }
}
</script>
<style lang="scss">
  .connection-card-wrap {
    padding: 0.5rem;
    display: flex;
    width: 100%;
    cursor: pointer;

    .itsme {
      .connection-card__info--name { color: $colorBlue; }
    }

    @media (min-width: '768px') {
      width: 50%;
    }
    @media (min-width: '1200px') {
      width: 33.333%;
    }
  }

</style>
<style scoped lang="scss">
  .connections-section {
    &:not(:last-child) {
      margin-bottom: 1rem;
    }
    &--header {
      &__title { max-width: 650px; }
      p { font-size: 0.875rem; opacity: 0.6; }
      display: flex;
      justify-content: space-between;
      flex-direction: column;
      margin-bottom: 1rem;
      .btn-primary { width: 100%; }

      @media (min-width: 576px) {
        flex-direction: row;
        margin-bottom: 0;
        .btn-primary { width: auto; }
      }
    }
  }
  .connections-list {
    display: flex;
    flex-wrap: wrap;
    margin-left: -0.5rem;
    margin-right: -0.5rem;
  }
  .connections-list + .connections-list {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid $colorBorderDash;
  }
  .upgrade-teamsize-content-wrap {
    display: flex; 
    flex-direction: row;
    flex: 1; 
    gap: 1rem; 
    align-items: center;
  }
</style>
